<script setup lang="ts">
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { ref, Ref } from "vue";
import { useApiClient } from "@/api/client";
import { debounce } from "vue-debounce";
import { toastApiResult } from "@/toaster";
import ExportScheduleDO = App.Data.ExportScheduler.ExportScheduleDO;
import dayjs from "dayjs";
import CheckInterval from "@/modules/alerts/components/CheckInterval.vue";
import useDialog from "@/ui/composables/useDialog";
import NewScheduleDialog from "@/modules/scheduler/components/NewScheduleDialog.vue";
import { formatNumber } from "chart.js/helpers";
import ExportTypeChip from "@/ui/components/exports/ExportTypeChip.vue";

const breadcrumbs = [
	{
		title: "Export Scheduler",
		disabled: true,
		to: "/scheduler",
	},
];

const search = ref("");
const itemsPerPage = ref(100);
const showNewExportDialog: Ref<boolean> = ref(false);

const headers = [
	{ title: "Title", key: "title", sortable: true },
	{ title: "Type", key: "export_type_name", sortable: false },
	{ title: "Schedule", key: "execution_interval_type", sortable: false },
	{ title: "Times Executed", key: "executions_count", sortable: true },
	{ title: "Last Exported", key: "executions_last_exported", sortable: true },
	{ title: "Recipients", key: "recipients_count", sortable: true },
	{ title: "Active", key: "is_active", sortable: true },
	{ title: "Actions", key: "actions", align: "center", sortable: false },
];

const isLoading = ref(false);
const items = ref<ExportScheduleDO[]>([]);
const totalItemCount = ref(0);
const page = ref(1);
const sortBy = ref([]);

const loadItems = debounce(async ({ page, itemsPerPage, sortBy, search }) => {
	isLoading.value = true;

	const client = useApiClient("exportSchedules");

	const response = await client.index({ page, itemsPerPage, sortBy, search });
	if (response.isOk()) {
		items.value = response.value.data;
		totalItemCount.value = response.value.total;
	}

	isLoading.value = false;
}, 300);

const removeExportSchedule = async (schedule: ExportScheduleDO) => {
	const confirm = await useDialog().confirm({
		title: "Please confirm",
		message:
			"Are you sure you permanently want to delete this scheduled export?",
		isDelete: true,
	});
	if (!confirm) return;

	const response = await toastApiResult(
		useApiClient("exportSchedules").delete(schedule.uuid)
	);
	if (response.isOk()) {
		successCallback();
	}
};

const successCallback = () => {
	loadItems({
		page: page.value,
		itemsPerPage: itemsPerPage.value,
		sortBy: sortBy.value,
		search: search.value,
	});
};
</script>

<template>
	<new-schedule-dialog
		v-if="showNewExportDialog"
		v-model="showNewExportDialog"
	/>

	<main-breadcrumbs :breadcrumbs="breadcrumbs" />

	<v-card class="d-flex flex-column ga-2 pa-4 pb-2">
		<div class="d-flex ga-2">
			<v-text-field
				v-model="search"
				label="Search"
				prepend-inner-icon="mdi-magnify"
				hide-details
				single-line
			/>
			<v-btn
				prepend-icon="fa: fa-light fa-plus"
				height="48"
				@click="showNewExportDialog = true"
			>
				{{ $t("scheduler.create_schedule") }}
			</v-btn>
		</div>
		<v-data-table-server
			v-model:items-per-page="itemsPerPage"
			v-model:page="page"
			v-model:sort-by="sortBy"
			:search="search"
			:headers="headers"
			:items="items"
			:items-length="totalItemCount"
			:loading="isLoading"
			@update:options="loadItems"
		>
			<template v-slot:item.title="{ item }">
				<router-link
					class="router-link-primary"
					:to="{
						name: 'scheduler.configuration',
						params: { schedule_uuid: item.uuid },
					}"
				>
					{{ item.title }}
				</router-link>
			</template>
			<template v-slot:item.export_type_name="{ item }">
				<ExportTypeChip :export-type="item.export_type.name_clean" />
			</template>
			<template v-slot:item.execution_interval_type="{ item }">
				<div class="d-flex align-center ga-2">
					<v-icon
						:icon="
							item.trigger_type === 'alert_based'
								? 'mdi-bell-ring-outline'
								: 'mdi-clock-outline'
						"
						size="small"
					/>
					<check-interval
						v-if="item.trigger_type === 'alert_based'"
						:check-interval-type="item.alerts?.[0]?.check_interval_type"
						:check-interval-specification="
							item.alerts?.[0]?.check_interval_specification
						"
					/>
					<check-interval
						v-else
						:check-interval-type="item.execution_interval_type"
						:check-interval-specification="
							item.execution_interval_specification
						"
					/>
				</div>
			</template>
			<template v-slot:item.executions_count="{ item }">
				<span class="number-mono">
					{{ formatNumber(item.executions_count, "default") }}
				</span>
			</template>
			<template v-slot:item.executions_last_exported="{ item }">
				<template v-if="!item?.latest_execution"> -</template>
				<template v-else>
					{{
						dayjs(item.latest_execution.created_at).format("DD MMM YYYY HH:mm")
					}}
				</template>
			</template>
			<template v-slot:item.recipients_count="{ item }">
				<div class="d-flex align-center ga-1 number-mono">
					<v-icon size="14">fa: fa-regular fa-user</v-icon>
					{{ formatNumber(item.recipients_count, "default") }}
				</div>
			</template>
			<template v-slot:item.is_active="{ item }">
				<v-chip
					:color="item.is_active ? 'success' : 'secondary-tonal'"
					label
					density="comfortable"
					variant="tonal"
				>
					{{ item.is_active ? "Active" : "Inactive" }}
				</v-chip>
			</template>
			<template v-slot:item.actions="{ item }">
				<v-btn
					size="32"
					rounded
					icon
					color="error"
					variant="text"
					@click="removeExportSchedule(item)"
				>
					<v-icon size="16">fa-light fa-trash-can </v-icon>
				</v-btn>
			</template>
		</v-data-table-server>
	</v-card>
</template>

<style scoped lang="scss"></style>
